import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Lock, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { Brand } from "@/components/layout/Brand";

const Field = ({ icon: Icon, children }) => (
  <div className="relative">
    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
    {children}
  </div>
);

const inputCls =
  "w-full h-11 rounded-md bg-[#0a1425] border border-slate-700 pl-10 pr-10 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

export default function Login() {
  const { login, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (isAdmin) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim() || !password) {
      setError("Enter your username and password.");
      return;
    }
    setLoading(true);
    try {
      const u = await login(username.trim(), password);
      toast.success(`Welcome, ${u.first_name || u.username}.`);
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      const msg = err.response ? errorMessage(err) : err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#0a1425]">
      <aside className="relative hidden lg:block overflow-hidden">
        <img src="/login-bg.jpg" alt="Aircraft on the apron at night" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1425]/70 via-[#0a1425]/60 to-[#0a1425]" />
        <div className="absolute inset-0 hud-grid opacity-40" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Brand />
          <div className="max-w-md animate-rise">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500 mb-4">EASA Part-66 · M1 — M17</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-50 leading-[1.05]">
              Aviation Academy control center.
            </h1>
            <p className="mt-5 text-slate-300 text-sm leading-relaxed">
              Manage students, the question catalog and exams from a single operations console.
            </p>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Learning4Change · admin.learning4change.pt</p>
        </div>
      </aside>

      <main className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm animate-rise" data-testid="login-form" noValidate>
          <div className="lg:hidden mb-10"><Brand /></div>
          <div className="flex items-center gap-2 text-amber-500 mb-3">
            <ShieldCheck className="h-4 w-4" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em]">Restricted access</span>
          </div>
          <h2 className="font-heading text-3xl font-bold text-slate-50">Sign in</h2>
          <p className="mt-2 text-sm text-slate-400">Administrator accounts only.</p>

          <div className="mt-10 space-y-5">
            <label className="block">
              <span className="block mb-2 text-xs font-medium text-slate-300">Username or email</span>
              <Field icon={User}>
                <input
                  className={inputCls}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  placeholder="Your username"
                  data-testid="login-username-input"
                />
              </Field>
            </label>
            <label className="block">
              <span className="block mb-2 text-xs font-medium text-slate-300">Password</span>
              <Field icon={Lock}>
                <input
                  type={show ? "text" : "password"}
                  className={inputCls}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-amber-400"
                  data-testid="login-toggle-password"
                  aria-label="Show password"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </Field>
            </label>
          </div>

          {error && (
            <p className="mt-5 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-testid="login-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="mt-8 w-full h-11 rounded-md bg-amber-500 text-[#0a1425] font-semibold text-sm flex items-center justify-center gap-2 transition-[background-color,transform] duration-200 hover:bg-amber-400 active:scale-[0.98] disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </main>
    </div>
  );
}
